//* Libraries imports
import { useEffect, useMemo, useState } from "react";

//* Utils imports
import { formatRemainingTime, formatExpirationDate } from "@/utils/formatDate";

type PixTimerProps = {
  totalMs?: number;
  expirationDate?: string | null;
  onExpire?: () => void;
};

export function PixTimer({
  totalMs = 90 * 60 * 1000,
  expirationDate,
  onExpire,
}: PixTimerProps) {
  const [remaining, setRemaining] = useState<number>(totalMs);

  useEffect(() => {
    setRemaining(totalMs);
  }, [totalMs]);

  const progress = useMemo(() => {
    if (totalMs === 0) return 1;
    return 1 - remaining / totalMs;
  }, [remaining, totalMs]);

  useEffect(() => {
    if (remaining <= 0) return;

    const interval = setInterval(() => {
      setRemaining((prev) => {
        const next = prev - 1000;
        if (next <= 0) {
          clearInterval(interval);
          if (onExpire) onExpire();
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [onExpire, remaining > 0]);

  return (
    <div className="w-full flex flex-col items-center gap-3">
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
          Expira em {expirationDate ? formatExpirationDate(expirationDate) : ""}
        </span>
        <span className="text-2xl font-bold font-mono text-claroprimaria-01 tabular-nums">
          {formatRemainingTime(remaining)}
        </span>
      </div>

      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden border border-gray-50">
        <div
          className={`h-full transition-all duration-1000 ease-linear ${
            remaining < 60000 ? "bg-red-500" : "bg-claroprimaria-01"
          }`}
          style={{ width: `${Math.max(0, Math.min(1, progress)) * 100}%` }}
        />
      </div>
    </div>
  );
}
